<div class="crm-dialog">
  <h2 mat-dialog-title>
    <mat-icon>table_view</mat-icon>
    Excel Oszlop Párosítás
  </h2>

  <mat-dialog-content>
    <div class="dialog-info">
      <p><strong>Fájl:</strong> {{ data.filename }}</p>
      <p><strong>Oszlopok száma:</strong> {{ data.columns.length }}</p>
      <p><strong>Sorok száma:</strong> {{ data.data.length }}</p>
    </div>

    <div class="mapping-instructions">
      <mat-icon color="primary">info</mat-icon>
      <span>Párosítsd az Excel oszlopokat az adatbázis mezőkkel.</span>
    </div>

    <div class="enrich-option">
      <mat-checkbox [(ngModel)]="enrichExisting" color="primary">
        Meglévő rekordok kiegészítése
      </mat-checkbox>
      <span class="enrich-hint">
        Ha a cég cégjegyzékszám vagy adószám alapján már létezik, a sor nem marad ki: a cég üres mezői
        kitöltődnek, és az új tisztségviselők, telefonszámok, email címek hozzáadódnak. Meglévő adat nem íródik felül.
      </span>
    </div>

    <!-- Loading spinner -->
    @if (isLoadingFields) {
      <div class="loading-container">
        <mat-spinner diameter="30"></mat-spinner>
        <span>Adatbázis mezők betöltése...</span>
      </div>
    }

    @if (!isLoadingFields) {
      <div class="mapping-container">
        <table mat-table [dataSource]="columnMappings" class="mapping-table">
          <!-- Excel oszlop -->
          <ng-container matColumnDef="excelColumn">
            <th mat-header-cell *matHeaderCellDef>Excel Oszlop</th>
            <td mat-cell *matCellDef="let mapping">
              <div class="excel-column">
                <strong>{{ mapping.excelColumn }}</strong>
                <div class="sample-data">
                  Minta: <em>{{ mapping.sampleData }}</em>
                </div>
                @if (mapping.isMultiValue) {
                  <div class="multi-value-indicator">
                    <mat-icon color="accent" style="font-size: 16px;">group</mat-icon>
                    <span>Többértékű mező</span>
                    @if (mapping.separator) {
                      <span class="separator-info">({{ mapping.separator }})</span>
                    }
                  </div>
                }
              </div>
            </td>
          </ng-container>
          <!-- Nyíl -->
          <ng-container matColumnDef="arrow">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let mapping">
              <mat-icon color="primary">arrow_forward</mat-icon>
            </td>
          </ng-container>
          <!-- Adatbázis mező -->
          <ng-container matColumnDef="databaseColumn">
            <th mat-header-cell *matHeaderCellDef>Adatbázis Mező</th>
            <td mat-cell *matCellDef="let mapping; let i = index">
              <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
                <mat-select [(ngModel)]="mapping.databaseColumn"
                  [ngModelOptions]="{standalone: true}"
                  (ngModelChange)="onMappingChange()"
                  [placeholder]="mapping.isRequired ? 'Kötelező mező' : 'Válassz mezőt'">
                  <mat-option value="">-- Kihagyás --</mat-option>
                  @for (dbField of databaseFields; track dbField) {
                    <mat-option
                      [value]="dbField.value"
                      [disabled]="isFieldAlreadyMapped(dbField.value, i)">
                      {{ dbField.label }}
                      @if (dbField.required) {
                        <span class="required-indicator">*</span>
                      }
                      <span class="field-table">({{ dbField.table }})</span>
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
        </table>
      </div>
    }

    @if (validationErrors.length > 0) {
      <div class="validation-errors">
        <h4><mat-icon color="warn">warning</mat-icon> Figyelem:</h4>
        <ul>
          @for (error of validationErrors; track error) {
            <li class="error-item">
              {{ error }}
            </li>
          }
        </ul>
      </div>
    }

    <!-- Kombinált mezők kezelése -->
    @if (!isLoadingFields) {
      <div class="combined-fields-section">
        <h4>
          <mat-icon color="primary">merge_type</mat-icon>
          Kombinált mezők
          <button mat-icon-button
            color="primary"
            (click)="addCombinedField()"
            matTooltip="Új kombinált mező hozzáadása">
            <mat-icon>add</mat-icon>
          </button>
        </h4>
        <p class="combined-info">
          Több Excel oszlopot kombinálhatsz egy adatbázis mezőbe (pl. város + utca + házszám → cím)
        </p>
        @for (combined of combinedFields; track combined; let i = $index) {
          <div class="combined-field-item">
            <div class="combined-header">
              <mat-form-field appearance="outline" class="target-field" subscriptSizing="dynamic">
                <mat-label>Cél adatbázis mező</mat-label>
                <mat-select [(ngModel)]="combined.targetDatabaseField"
                  [ngModelOptions]="{standalone: true}">
                  @for (dbField of databaseFields; track dbField) {
                    <mat-option
                      [value]="dbField.value"
                      [disabled]="isCombinedFieldUsed(dbField.value, i)">
                      {{ dbField.label }}
                      <span class="field-table">({{ dbField.table }})</span>
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button mat-icon-button
                color="warn"
                (click)="removeCombinedField(i)"
                matTooltip="Kombinált mező törlése">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
            <div class="combined-sources">
              <h5>Forrás oszlopok:</h5>
              <div class="source-columns">
                <mat-chip-set>
                  @for (column of combined.sourceColumns; track column; let j = $index) {
                    <mat-chip
                      [removable]="true"
                      (removed)="removeSourceColumn(i, j)">
                      {{ column }}
                      <mat-icon matChipRemove>cancel</mat-icon>
                    </mat-chip>
                  }
                </mat-chip-set>
                <mat-form-field appearance="outline" class="add-column" subscriptSizing="dynamic">
                  <mat-label>Oszlop hozzáadása</mat-label>
                  <mat-select (selectionChange)="addSourceColumn(i, $event.value)">
                    <mat-option value="">-- Válassz oszlopot --</mat-option>
                    @for (column of getAvailableColumns(i); track column) {
                      <mat-option
                        [value]="column">
                        {{ column }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
            </div>
            <div class="combined-template">
              <mat-form-field appearance="outline" class="template-field" subscriptSizing="dynamic">
                <mat-label>Sablon (pl: "{{ '{0}' }}, {{ '{1}' }} {{ '{2}' }}")</mat-label>
                <input matInput [(ngModel)]="combined.template"
                  [ngModelOptions]="{standalone: true}"
                placeholder="{{ '{0}' }}, {{ '{1}' }} {{ '{2}' }}">          </mat-form-field>
                <mat-form-field appearance="outline" class="separator-field" subscriptSizing="dynamic">
                  <mat-label>Elválasztó</mat-label>
                  <input matInput [(ngModel)]="combined.separator"
                    [ngModelOptions]="{standalone: true}"
                    placeholder=", ">
                </mat-form-field>
              </div>
              @if (combined.sourceColumns.length > 0) {
                <div class="combined-preview">
                  <strong>Előnézet:</strong>
                  <span class="preview-text">{{ getCombinedPreview(combined) }}</span>
                </div>
              }
            </div>
          }
        </div>
      }

      @if (!isLoadingFields) {
        <div class="mapping-summary">
          <h4>Párosítás összefoglalása:</h4>
          <div class="summary-stats">
            <div class="stat">
              <span class="stat-label">Párosított oszlopok:</span>
              <span class="stat-value">{{ getMappedCount() }}/{{ columnMappings.length }}</span>
            </div>
            <div class="stat">
              <span class="stat-label">Kötelező mezők:</span>
              <span class="stat-value">{{ getRequiredMappedCount() }}/{{ getRequiredCount() }}</span>
            </div>
          </div>
        </div>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Mégse</button>
      <button mat-button (click)="onPreview()" [disabled]="!isValidMapping() || isLoadingFields">
        <mat-icon>preview</mat-icon>
        Előnézet
      </button>
      <button mat-raised-button
        color="primary"
        (click)="onConfirm()"
        [disabled]="!isValidMapping() || isLoadingFields">
        <mat-icon>upload</mat-icon>
        Importálás ({{ data.data.length }} sor)
      </button>
    </mat-dialog-actions>
  </div>
